import * as React from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

interface TablePaginationProps {
  page: number;
  pageCount: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  itemLabel?: string;
  className?: string;
}

function getPageNumbers(page: number, pageCount: number): (number | "...")[] {
  const delta = 2;
  const range: number[] = [];

  for (
    let i = Math.max(2, page - delta);
    i <= Math.min(pageCount - 1, page + delta);
    i++
  ) {
    range.push(i);
  }

  const pages: (number | "...")[] = [1];
  if (page - delta > 2) pages.push("...");
  pages.push(...range);
  if (page + delta < pageCount - 1) pages.push("...");
  if (pageCount > 1) pages.push(pageCount);

  return pages;
}

export function TablePagination({
  page,
  pageCount,
  totalItems,
  pageSize,
  onPageChange,
  itemLabel = "registros",
  className,
}: TablePaginationProps) {
  if (pageCount <= 1) return null;

  const startIndex = (page - 1) * pageSize;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-between gap-3 sm:flex-row",
        className
      )}
    >
      <p className="text-sm text-muted-foreground">
        Mostrando {startIndex + 1}–{Math.min(startIndex + pageSize, totalItems)}{" "}
        de {totalItems} {itemLabel}
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-9 gap-1"
          onClick={() => onPageChange(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Anterior
        </Button>

        {getPageNumbers(page, pageCount).map((pageNumber, index) =>
          pageNumber === "..." ? (
            <span
              key={`ellipsis-${index}`}
              className="px-2 text-sm text-muted-foreground"
            >
              …
            </span>
          ) : (
            <Button
              key={pageNumber}
              variant={pageNumber === page ? "outline" : "ghost"}
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => onPageChange(pageNumber)}
            >
              {pageNumber}
            </Button>
          )
        )}

        <Button
          variant="ghost"
          size="sm"
          className="h-9 gap-1"
          onClick={() => onPageChange(Math.min(page + 1, pageCount))}
          disabled={page === pageCount}
        >
          Siguiente
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
